import React from 'react';

import { STAT_ICON_GRADIENT } from '../../../../theme/tokens';
import type { AutomatedStatIcon } from '../../../../mock-data/types';

export type StatIconProps = {
  name: AutomatedStatIcon;
  size?: number;
  className?: string;
};

/**
 * The five "Automated Operations" stat glyphs (megaphone / people / bell /
 * member / handshake) — the exact Figma vectors, each filled with the shared
 * diagonal charcoal→grey gradient (STAT_ICON_GRADIENT). Kept local to the
 * Operations stats panel rather than in the shared `Icon` set because, unlike
 * that single-`color` line/fill set, these are multi-path gradient glyphs (the
 * member's check badge and the bell's two ring-arcs need their own per-path
 * fills to read). Every gradient id is prefixed so the icons never resolve each
 * other's `url(#…)` fill on the page.
 */

// One stop pair (tokenised), reused by every gradient so no raw hex appears.
const Stops = (): React.ReactElement => (
  <>
    <stop stopColor={STAT_ICON_GRADIENT.from} />
    <stop offset={1} stopColor={STAT_ICON_GRADIENT.to} />
  </>
);

export const StatIcon = ({ name, size = 30, className }: StatIconProps): React.ReactElement | null => {
  const svg = (viewBox: string, body: React.ReactNode, defs: React.ReactNode): React.ReactElement => (
    <svg className={className} width={size} height={size} viewBox={viewBox} fill="none" xmlns="http://www.w3.org/2000/svg">
      {body}
      <defs>{defs}</defs>
    </svg>
  );

  switch (name) {
    case 'megaphone':
      return svg(
        '0 0 30 30',
        <path
          fillRule="evenodd"
          clipRule="evenodd"
          d="M10.4706 22.3441L13.257 25.9655C13.51 26.2942 13.4656 26.7595 13.1584 27.0382L12.2002 27.9078C12.029 28.0631 11.823 28.1322 11.5929 28.1114C11.3628 28.0906 11.1732 27.9852 11.032 27.8023L7.94273 23.8035L10.4706 22.3441ZM25.7559 13.0501H28.5937C29.0477 13.0501 29.415 12.6793 29.4141 12.226C29.4131 11.7721 29.043 11.4053 28.5896 11.4053H25.7518C25.2978 11.4053 24.9305 11.776 24.9315 12.2293C24.9323 12.6833 25.3025 13.0501 25.7559 13.0501ZM22.3874 5.57807L23.8063 3.12041C24.033 2.72777 23.9005 2.22387 23.5078 1.99606C23.1157 1.76859 22.611 1.90131 22.384 2.29441L20.9651 4.75207C20.7384 5.14471 20.8709 5.64861 21.2636 5.87643C21.6557 6.10389 22.1604 5.97117 22.3874 5.57807ZM25.6707 8.52193L28.0006 7.1768C28.3961 6.94846 28.5316 6.44285 28.3032 6.04728C28.0749 5.65178 27.5693 5.51631 27.1737 5.74465L24.8439 7.08984C24.4484 7.31818 24.3129 7.82379 24.5413 8.21935C24.7696 8.6148 25.2752 8.75027 25.6707 8.52193ZM22.0064 12.2232L24.7544 16.983C25.2704 17.8766 24.9613 19.0299 24.0677 19.5459C23.2338 20.0273 22.1739 19.7903 21.6166 19.0303C18.5748 18.5676 14.6123 19.0646 10.6723 20.6006L6.88447 14.0399C10.1859 11.3985 12.605 8.23236 13.7252 5.36672C13.3317 4.50023 13.655 3.45129 14.4954 2.96607C15.3891 2.4501 16.5424 2.75918 17.0583 3.65279L19.8064 8.4126C19.8846 8.35213 19.9677 8.29617 20.0558 8.24537C21.1176 7.6323 22.4754 7.99611 23.0885 9.05801C23.7016 10.1199 23.3378 11.4778 22.2759 12.0908C22.1879 12.1416 22.0979 12.1857 22.0064 12.2232ZM9.71988 21.2946L5.7508 14.42L2.59547 16.2418C0.678164 17.3487 0.0152342 19.8231 1.12213 21.7403C2.22908 23.6576 4.70344 24.3206 6.62074 23.2137L9.77607 21.392L9.71988 21.2946Z"
          fill="url(#stat-megaphone)"
        />,
        <linearGradient id="stat-megaphone" x1="1.00003" y1="1.50019" x2="29" y2="28.5002" gradientUnits="userSpaceOnUse">
          <Stops />
        </linearGradient>,
      );
    case 'people':
      return svg(
        '0 0 29.8963 25.1218',
        <>
          <path
            d="M15.2213 13.2172C17.6514 13.2172 19.6211 11.2471 19.6211 8.81734C19.6211 6.38753 17.6511 4.41786 15.2213 4.41786C12.7915 4.41786 10.8207 6.38791 10.8207 8.81771C10.8207 11.2475 12.7915 13.2172 15.2213 13.2172ZM17.0876 13.5171H13.3542C10.2478 13.5171 7.7207 16.0446 7.7207 19.1509V23.7166L7.73231 23.7881L8.0468 23.8866C11.0112 24.8128 13.5867 25.1217 15.7065 25.1217C19.8469 25.1217 22.2467 23.9413 22.3946 23.866L22.6885 23.7174H22.72V19.1509C22.7211 16.0446 20.1939 13.5171 17.0876 13.5171Z"
            fill="url(#stat-people-front)"
          />
          <path
            d="M9.84088 8.11981C9.16249 8.54774 8.36166 8.79895 7.50094 8.79895C5.07039 8.79895 3.10109 6.8289 3.10109 4.39985C3.10109 1.96967 5.07039 0 7.50094 0C9.92999 0 11.9 1.96967 11.9 4.39985C11.9 4.48297 11.8922 4.56458 11.8877 4.64695C10.8106 5.48409 10.0565 6.71322 9.84088 8.11981Z"
            fill="url(#stat-people-left)"
          />
          <path
            d="M9.77986 9.11943C9.82591 10.5927 10.4564 11.9203 11.4523 12.8753C8.69038 13.6971 6.67091 16.2568 6.67016 19.2812V20.6601C4.82666 20.5522 2.70423 20.21 0.326095 19.4676L0.0116061 19.3684L0 19.298V14.7316C0 11.6256 2.52714 9.09846 5.63347 9.09846H9.36653C9.50655 9.09846 9.64246 9.10932 9.77986 9.11943Z"
            fill="url(#stat-people-left)"
          />
          <path
            d="M20.5369 8.383C20.3598 6.67465 19.3871 5.20292 18 4.33321C18.0367 1.93523 19.9891 0.000748783 22.3961 0C24.8263 0 26.796 1.96967 26.796 4.39985C26.796 6.82965 24.8263 8.79933 22.3961 8.79933C21.7304 8.79933 21.1026 8.64732 20.5369 8.383Z"
            fill="url(#stat-people-right)"
          />
          <path
            d="M20.5586 9.09959H24.2632C27.3692 9.09959 29.8963 11.6267 29.8963 14.7327V19.2999H29.8648L29.5709 19.4489C29.432 19.5185 27.3242 20.5552 23.6664 20.6893V19.2823C23.6664 16.258 21.6462 13.6975 18.8851 12.8765C19.8858 11.9165 20.5185 10.5818 20.5586 9.09959Z"
            fill="url(#stat-people-right)"
          />
        </>,
        <>
          <linearGradient id="stat-people-front" x1="29.5691" y1="25.4939" x2="2.62485" y2="-2.59783" gradientUnits="userSpaceOnUse">
            <Stops />
          </linearGradient>
          <linearGradient id="stat-people-left" x1="0.430896" y1="-0.370336" x2="27.3752" y2="27.7214" gradientUnits="userSpaceOnUse">
            <Stops />
          </linearGradient>
          <linearGradient id="stat-people-right" x1="30.4812" y1="26.1835" x2="3.53696" y2="-1.90827" gradientUnits="userSpaceOnUse">
            <Stops />
          </linearGradient>
        </>,
      );
    case 'bell':
      // Composed in a 30×30 box: the main bell (native 25×30, centred) plus the
      // two flat ring-arcs placed symmetrically just outside its rim.
      return svg(
        '0 0 30 30',
        <>
          <g transform="translate(2.5 0)">
            <path
              d="M24.2238 21.1413C22.3338 19.5438 21.25 17.2088 21.25 14.735V11.25C21.25 6.85125 17.9825 3.21 13.75 2.6V1.25C13.75 0.55875 13.19 0 12.5 0C11.81 0 11.25 0.55875 11.25 1.25V2.6C7.01625 3.21 3.75 6.85125 3.75 11.25V14.735C3.75 17.2088 2.66625 19.5438 0.765 21.1513C0.27875 21.5675 0 22.1725 0 22.8125C0 24.0188 0.98125 25 2.1875 25H22.8125C24.0188 25 25 24.0188 25 22.8125C25 22.1725 24.7213 21.5675 24.2238 21.1413Z"
              fill="url(#stat-bell-body)"
            />
            <path
              d="M12.5 30C14.7638 30 16.6575 28.3862 17.0925 26.25H7.9075C8.3425 28.3862 10.2363 30 12.5 30Z"
              fill="url(#stat-bell-clapper)"
            />
          </g>
          <path
            transform="translate(1.251 0.936)"
            d="M1.25 12.6653C0.56 12.6653 0 12.1053 0 11.4153C0 7.24156 1.625 3.31781 4.57625 0.366563C5.065 -0.122188 5.855 -0.122188 6.34375 0.366563C6.8325 0.855313 6.8325 1.64531 6.34375 2.13406C3.865 4.61281 2.5 7.90906 2.5 11.4153C2.5 12.1053 1.94 12.6653 1.25 12.6653Z"
            fill={STAT_ICON_GRADIENT.from}
          />
          <path
            transform="translate(22.039 0.936)"
            d="M5.46031 12.6653C4.77031 12.6653 4.21031 12.1053 4.21031 11.4153C4.21031 7.90906 2.84531 4.61406 0.366563 2.13406C-0.122188 1.64531 -0.122188 0.855313 0.366563 0.366563C0.855313 -0.122188 1.64531 -0.122188 2.13406 0.366563C5.08532 3.31781 6.71031 7.24156 6.71031 11.4153C6.71031 12.1053 6.15031 12.6653 5.46031 12.6653Z"
            fill={STAT_ICON_GRADIENT.from}
          />
        </>,
        <>
          <linearGradient id="stat-bell-body" x1="0.35908" y1="-0.366723" x2="26.8368" y2="22.8644" gradientUnits="userSpaceOnUse">
            <Stops />
          </linearGradient>
          <linearGradient id="stat-bell-clapper" x1="8.03943" y1="26.195" x2="11.1038" y2="32.7804" gradientUnits="userSpaceOnUse">
            <Stops />
          </linearGradient>
        </>,
      );
    case 'member':
      return svg(
        '0 0 30 30',
        <>
          <path
            d="M17.6143 13.7451C21.9632 13.7452 25.5015 17.2858 25.5 21.6367V28.0332H25.4561L25.0439 28.2412C24.8338 28.3481 21.4744 29.9999 15.6807 30C12.713 30 9.10709 29.5677 4.95703 28.2705L4.5166 28.1318L4.5 28.0312V21.6367C4.50006 17.2857 8.03868 13.7451 12.3877 13.7451H17.6143ZM15.1572 15.2275C14.5626 15.2276 13.9969 15.4292 13.1201 15.7295L12.7861 15.8438C11.9169 16.1414 11.24 16.3732 10.7549 16.5703C10.5119 16.6691 10.2967 16.7672 10.1211 16.8691C9.95428 16.9661 9.77083 17.0959 9.64258 17.2783C9.51598 17.4588 9.45449 17.6747 9.41797 17.8643C9.37963 18.0635 9.35711 18.2984 9.34277 18.5615C9.31416 19.0873 9.31445 19.8071 9.31445 20.7324V21.6523C9.3147 25.3084 12.0791 27.0548 13.6943 27.7607C13.8961 27.8489 14.0983 27.9391 14.332 27.999C14.5744 28.0611 14.8288 28.0859 15.1572 28.0859C15.4853 28.0859 15.7393 28.0609 15.9814 27.999C16.2153 27.9391 16.4184 27.8489 16.6201 27.7607C18.2353 27.0548 20.9998 25.3083 21 21.6523V20.7324C21 19.8071 20.9993 19.0873 20.9707 18.5615C20.9564 18.2984 20.9348 18.0634 20.8965 17.8643C20.86 17.6747 20.7985 17.4589 20.6719 17.2783C20.5435 17.0957 20.3593 16.9661 20.1924 16.8691C20.0169 16.7672 19.8024 16.669 19.5596 16.5703C19.0744 16.3732 18.3974 16.1414 17.5283 15.8438L17.1934 15.7295C16.3169 15.4293 15.7517 15.2276 15.1572 15.2275Z"
            fill="url(#stat-member-body)"
          />
          <path
            fillRule="evenodd"
            clipRule="evenodd"
            d="M15.1577 16.4141C15.5096 16.4141 15.859 16.5259 16.8103 16.8517L17.1443 16.966C18.0285 17.2687 18.6672 17.4883 19.114 17.6699C19.3368 17.7604 19.493 17.834 19.5985 17.8953C19.6554 17.9283 19.6863 17.951 19.7004 17.9624C19.7065 17.979 19.7198 18.0176 19.7336 18.089C19.7571 18.2112 19.7759 18.3849 19.7891 18.627C19.8155 19.1127 19.8153 19.7929 19.8153 20.7335V21.6528C19.8152 24.5769 17.6344 26.0243 16.1468 26.6746C15.9279 26.7702 15.8135 26.8185 15.6885 26.8505C15.572 26.8803 15.4232 26.9007 15.1577 26.9007C14.892 26.9007 14.7434 26.8804 14.6269 26.8505C14.5019 26.8185 14.388 26.7701 14.1693 26.6746C12.6817 26.0244 10.5002 24.577 10.5 21.6528V20.7335C10.5 19.7929 10.5006 19.1127 10.527 18.627C10.5402 18.3849 10.559 18.2112 10.5826 18.089C10.5962 18.0182 10.6081 17.9792 10.6142 17.9624C10.628 17.9512 10.6593 17.9287 10.7168 17.8953C10.8223 17.834 10.9785 17.7604 11.2013 17.6699C11.6481 17.4883 12.2868 17.2687 13.171 16.966L13.5051 16.8517C14.4563 16.5259 14.8058 16.4141 15.1577 16.4141ZM17.0109 20.048C16.7668 19.8301 16.3917 19.8509 16.1738 20.0951L14.5327 21.9345L14.1416 21.4961C13.9236 21.2519 13.5486 21.231 13.3045 21.449C13.0607 21.6671 13.0396 22.0416 13.2574 22.2857L14.0907 23.2197C14.203 23.3456 14.364 23.4172 14.5327 23.4173C14.7014 23.4172 14.8624 23.3455 14.9748 23.2197L17.0579 20.8847C17.2758 20.6405 17.2549 20.2661 17.0109 20.048Z"
            fill="url(#stat-member-torso)"
          />
          <path
            d="M15.0007 2.5C18.0378 2.5 20.5 4.96253 20.5 8C20.5 11.0375 18.0382 13.5 15.0007 13.5C11.9637 13.5 9.50014 11.038 9.5 8.00069C9.5 4.96324 11.9636 2.50004 15.0007 2.5Z"
            fill="url(#stat-member-head)"
          />
        </>,
        <>
          <linearGradient id="stat-member-body" x1="4.80163" y1="13.5067" x2="22.0294" y2="33.0345" gradientUnits="userSpaceOnUse">
            <Stops />
          </linearGradient>
          <linearGradient id="stat-member-torso" x1="29.9256" y1="5.30184" x2="12.1088" y2="33.3446" gradientUnits="userSpaceOnUse">
            <Stops />
          </linearGradient>
          <linearGradient id="stat-member-head" x1="1.67445" y1="24.5429" x2="17.5734" y2="-0.489348" gradientUnits="userSpaceOnUse">
            <Stops />
          </linearGradient>
        </>,
      );
    case 'handshake':
      return svg(
        '0 0 30 27.9587',
        <>
          <path
            d="M8.15104 19.2278C7.73471 18.7946 7.00569 18.7408 6.52984 19.1021L6.51734 19.094L5.44046 20.1709C5.20434 20.4062 5.07488 20.7203 5.07488 21.0543C5.06069 21.7268 5.65282 22.3176 6.32462 22.304C6.71572 22.3103 7.06402 22.1148 7.31911 21.8274L8.15104 20.9954C8.32537 20.8211 8.44233 20.6041 8.49088 20.368C8.57804 19.9596 8.44917 19.5216 8.15104 19.2278Z"
            fill="url(#stat-hand-0)"
          />
          <path
            d="M5.15572 15.5669C4.66826 15.0794 3.8762 15.0799 3.38814 15.5669C3.27472 15.6809 2.57534 16.3799 2.44588 16.5099C1.3217 17.7299 2.99204 19.4009 4.21272 18.2775C4.21272 18.2775 5.03053 17.4596 5.15572 17.3345C5.64348 16.8475 5.64348 16.0546 5.15572 15.5669Z"
            fill="url(#stat-hand-1)"
          />
          <path
            d="M10.7064 22.0782C10.3725 22.0782 10.0584 22.2084 9.82226 22.4445L8.87926 23.3875C8.87484 23.3919 8.87043 23.3963 8.86602 23.4015C8.39349 23.8684 8.40055 24.6951 8.87926 25.1551C9.36694 25.642 10.1599 25.642 10.6468 25.1551L11.5906 24.2113C12.3722 23.4317 11.8104 22.074 10.7064 22.0782Z"
            fill="url(#stat-hand-2)"
          />
          <path
            d="M19.0934 8.20855C17.164 9.42519 14.577 9.19422 12.8977 7.51491L10.7116 5.32952L13.4398 2.60129C10.5095 -0.687086 5.35683 -0.895694 2.23771 2.24461C-0.898326 5.35931 -0.697295 10.5059 2.58416 13.4371C4.97389 12.1014 7.91257 13.967 7.71919 16.6883C9.30155 16.8593 10.6412 18.2935 10.7137 19.8763C12.2017 19.8444 13.6797 20.9588 14.0422 22.4287C15.2809 22.0967 16.6693 22.508 17.5177 23.4894L17.5427 23.46C18.7727 24.1984 20.0739 22.6817 19.1463 21.5769L19.1485 21.574C19.1294 21.5556 14.1812 16.6074 14.1812 16.6074C14.57 16.2186 15.3526 15.436 15.7413 15.0472L20.652 19.9579C21.872 21.0819 23.5433 19.4104 22.4189 18.1903L17.5089 13.2797C17.8977 12.8908 18.6803 12.1083 19.0691 11.7195C20.1727 12.8231 22.8502 15.5007 23.9797 16.6302C24.4448 17.1146 25.2822 17.1144 25.7473 16.6302C26.2343 16.1425 26.2343 15.3496 25.7473 14.8626L19.0934 8.20855Z"
            fill="url(#stat-hand-3)"
          />
          <path
            d="M27.7621 2.24202C24.772 -0.74734 19.907 -0.74734 16.9169 2.24202C16.3355 2.82379 14.3817 4.77798 13.8326 5.32701L14.4578 5.95224C15.545 7.03942 17.3133 7.03942 18.3998 5.95224L19.1802 5.1718C19.5952 5.5913 27.2119 13.1951 27.426 13.4242C30.6996 10.4919 30.8935 5.35268 27.7621 2.24202Z"
            fill="url(#stat-hand-4)"
          />
          <path
            d="M15.8054 24.8851C15.3176 24.3981 14.5253 24.3975 14.0378 24.8851L13.0948 25.8281C12.6096 26.2933 12.6098 27.1305 13.0948 27.5957C13.5607 28.0799 14.3969 28.0796 14.8624 27.5957L15.8054 26.6527C16.2896 26.1881 16.2896 25.3486 15.8054 24.8851Z"
            fill="url(#stat-hand-5)"
          />
        </>,
        <>
          <linearGradient id="stat-hand-0" x1="5.12408" y1="18.8127" x2="8.7688" y2="22.0124" gradientUnits="userSpaceOnUse">
            <Stops />
          </linearGradient>
          <linearGradient id="stat-hand-1" x1="2.12369" y1="15.1509" x2="5.77473" y2="18.3543" gradientUnits="userSpaceOnUse">
            <Stops />
          </linearGradient>
          <linearGradient id="stat-hand-2" x1="8.56527" y1="22.0277" x2="12.2104" y2="25.2226" gradientUnits="userSpaceOnUse">
            <Stops />
          </linearGradient>
          <linearGradient id="stat-hand-3" x1="0.37506" y1="-0.339348" x2="25.6224" y2="24.1212" gradientUnits="userSpaceOnUse">
            <Stops />
          </linearGradient>
          <linearGradient id="stat-hand-4" x1="14.0648" y1="-0.196918" x2="28.3825" y2="14.9323" gradientUnits="userSpaceOnUse">
            <Stops />
          </linearGradient>
          <linearGradient id="stat-hand-5" x1="12.7803" y1="24.4692" x2="16.4225" y2="27.6634" gradientUnits="userSpaceOnUse">
            <Stops />
          </linearGradient>
        </>,
      );
    default:
      return null;
  }
};

export default StatIcon;
